"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import BlackPill from "@/components/getting-started/BlackPill";
import Card from "@/components/getting-started/Card";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import griBGimg from "@/public/lp2/component1/grid-bg.svg";
import blurBGimg from "@/public/lp2/component1/blur-bg.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function Component1Section({ data }: { data: PageHomeSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const component1TL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      component1TL
        .from(content1Ref.current.children, {
          duration: 1,
          stagger: 0.2,
          delay: 0.5,
          scrollTrigger: {
            trigger: content1Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content2Ref.current.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.5,
          scrollTrigger: {
            trigger: content2Ref.current,
            end: "center center",
            scrub: 1,
          },
        });

      const cards = gsap.utils.toArray(".experience-card");

      cards.forEach((card) => {
        component1TL.from(card as any, {
          y: "2rem",
          delay: 0.1,
          scrollTrigger: {
            // @ts-ignore
            trigger: card,
            end: "center center",
            scrub: 1,
          },
        });
      });
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      className="w-full relative z-[1] overflow-hidden bg-black mix-blend-lighten lg:pt-[10rem]"
      id="component-1"
    >
      <div className="container-lp mx-auto">
        {/* Backgrounds */}
        <div
          ref={content1Ref}
          className="relative w-full mb-[-16rem] md:mb-[-20rem] lg:mb-[-20rem] xl:mb-[-28rem] 2xl:mb-[-18rem]"
        >
          <HLSVideoPlayer
            controls={false}
            playsInline={true}
            autoPlay={true}
            muted={true}
            loop={true}
            className="w-full h-[70vh] lg:h-auto !max-w-[1920px] mx-auto object-cover pointer-events-none mix-blend-lighten"
            source={{
              embed: false,
              muxVideo: {
                assetId: "",
                filename: "video-bg",
                status: "ready",
                playbackId: "vY01sC8OOFYSQTiDi2IRL00MB02JU9EfC6uidNsJb4bjOc",
              },
              embedUrl: "",
              thumbnail: {
                src: "",
                width: 0,
                height: 0,
                lqip: "",
                name: "",
              },
            }}
          />

          <Image
            src={griBGimg}
            alt="Grid background"
            className="absolute z-[-3] left-0 right-0 bottom-0 w-full h-[50vh] lg:h-auto object-cover pointer-events-none"
          />

          <Image
            src={blurBGimg}
            alt="Blur background"
            className="absolute z-[-2] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] pointer-events-none w-max max-w-max lg:w-full lg:max-w-full"
          />
        </div>
        {/* End Backgrounds */}
        <div className="flex flex-col items-center justify-center mx-auto relative z-[1]">
          <div
            ref={content2Ref}
            className="w-full md:w-4/5 lg:w-[70%] flex flex-col items-center justify-center mx-auto mb-[10rem] lg:mb-[20rem]"
          >
            {data?.component1Texts && (
              <PortableText
                value={data?.component1Texts ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill text={children as string} />
                    ),
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.35rem] lg:tracking-[-0.49rem] lg:leading-[8.875rem] leading-[110%] text-center mt-[4rem] mb-[3rem] sm:px-0 md:px-[10rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-[2rem] font-[400] leading-[150%] text-white opacity-75 text-center lg:w-[90%] mx-auto">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    orangeFlare: ({ children }) => (
                      <span className="orange-shadow-2">{children}</span>
                    ),
                  },
                })}
              />
            )}
          </div>
          <div className="flex flex-col items-center justify-center mx-auto z-[1] gap-[6rem] lg:gap-[15rem]">
            {data?.component1Items?.map((card, index) => (
              <Card
                key={index}
                assetImg={card?.image}
                imageOrder={index % 2 === 0 ? 1 : 2}
                titleClasses="text-[4rem] font-[600] tracking-[-0.16rem] lg:font-[700] leading-[120%] lg:leading-[110%]"
                hideCTAonMobile={true}
                cardClass="experience-card"
                ctaData={card?.CTA}
                texts={card?.texts}
              />
            ))}
          </div>
        </div>
      </div>

    </section>
  );
}
